import React, { useMemo } from "react";
import DOMPurify from "dompurify";
import { FaExpand, FaExternalLinkAlt, FaFilePdf } from "react-icons/fa";

import Reveal from "../../../components/ui/Reveal";

import PublicationAdaptiveImage from "./PublicationAdaptiveImage";

export default function PublicationDetail({ publication, onZoom }) {
  const sanitizedDescription = useMemo(() => {
    return DOMPurify.sanitize(publication?.descriptionRaw || "");
  }, [publication]);

  if (!publication) return null;

  return (
    <article id="publication-detail" className="scroll-mt-24">
      <Reveal>
        <PublicationAdaptiveImage
          src={publication.image}
          alt={publication.imageAlt}
          zoomable
          onClick={onZoom}
        />

        <div className="mt-3 flex justify-end">
          <button
            type="button"
            onClick={onZoom}
            className="inline-flex min-h-10 items-center justify-center gap-2 rounded-2xl bg-slate-950/80 px-4 text-sm font-bold text-white shadow-sm transition hover:bg-slate-950"
          >
            <FaExpand className="h-3.5 w-3.5" />
            Agrandir le visuel
          </button>
        </div>
      </Reveal>

      <Reveal>
        <section className="mt-6 rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950 sm:p-6">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center rounded-full bg-[#1277B9] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white">
              Publication municipale
            </span>

            {publication.dateLabel && (
              <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-500 dark:bg-slate-900 dark:text-slate-300">
                Publié le {publication.dateLabel}
              </span>
            )}

            {publication.hasLink && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-500 dark:bg-slate-900 dark:text-slate-300">
                {publication.isPdf && <FaFilePdf className="h-3.5 w-3.5" />}
                {publication.isPdf ? "Document PDF" : "Lien associé"}
              </span>
            )}
          </div>

          <h2 className="mt-4 text-2xl font-bold tracking-tight text-slate-950 dark:text-white md:text-3xl">
            {publication.title}
          </h2>

          <div className="mt-5">
            {sanitizedDescription ? (
              <div
                className="prose prose-sm max-w-none leading-relaxed text-slate-700 prose-headings:text-slate-950 prose-a:text-[#1277B9] prose-img:rounded-2xl dark:prose-invert dark:text-slate-200 sm:prose-base"
                dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
              />
            ) : (
              <p className="text-sm leading-7 text-slate-600 dark:text-slate-300">
                La description détaillée de cette publication sera prochainement
                disponible.
              </p>
            )}
          </div>

          {publication.hasLink && (
            <div className="mt-6">
              <a
                href={publication.link}
                target={publication.isExternal ? "_blank" : undefined}
                rel={publication.isExternal ? "noreferrer" : undefined}
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-[#1277B9] px-5 text-sm font-bold text-white shadow-sm transition hover:bg-[#0f659d]"
              >
                {publication.isPdf && <FaFilePdf className="h-4 w-4" />}
                Ouvrir la publication
                {publication.isExternal && (
                  <FaExternalLinkAlt className="h-3.5 w-3.5" />
                )}
              </a>
            </div>
          )}
        </section>
      </Reveal>
    </article>
  );
}
